import React from 'react';
import { motion } from 'framer-motion';
import { HelpCircleIcon, SparklesIcon, TargetIcon } from 'lucide-react';
import { masterSnaks } from '../../data/masterSnaks';
import { InteriorSlide, SlideProps } from './SlideShell';
const LIVE_SNAK_IMAGE = 'https://cdn.magicpatterns.com/uploads/cRFTvD5FPU1PTJPbZDPGuy/Screenshot_2026-09-09_at_12.59.35.png';
const COLUMNS = [{
  label: 'Teaser campaign',
  icon: SparklesIcon
}, {
  label: 'Hook question',
  icon: HelpCircleIcon
}, {
  label: 'Strategic purpose',
  icon: TargetIcon
}] as const;
const ROW_GRID = 'grid-cols-[1fr_1.25fr_1.4fr]';
export function SlideMasterSnaks({}: SlideProps) {
  return <InteriorSlide surface data-id="element-478" data-mp="35qvrpzpi3934">
      <motion.header initial={{
      opacity: 0,
      y: 16
    }} animate={{
      opacity: 1,
      y: 0
    }} transition={{
      duration: 0.5
    }} className="mb-[clamp(1rem,1.7vw,1.5rem)] flex flex-shrink-0 items-end justify-between gap-8" data-id="element-479" data-mp="35pktsgiqkvjx">
        <div data-id="element-480" data-mp="282iu07o38fa">
          <p className="mb-2 text-[clamp(0.7rem,1vw,0.9rem)] font-bold uppercase tracking-[0.2em] text-[var(--brand)]" data-id="element-481" data-mp="1o73ggq8kzopq">
            Teaser campaign roadmap · illustrative example
          </p>
          <h2 className="text-[clamp(2rem,3.3vw,3.2rem)] font-extrabold leading-tight tracking-[-0.04em] text-[var(--fg)]" data-id="element-482" data-mp="1fehyvdgoro2h">
            Five teaser campaigns,{' '}
            <span className="text-[var(--brand)]" data-id="element-483" data-mp="1jeaci4kxskep">one brand story</span>
          </h2>
        </div>
        <p className="hidden max-w-sm text-right text-sm leading-relaxed text-[var(--muted)] lg:block" data-id="element-484" data-mp="15f0tyy99de7k">
          Each month opens with a hook question that earns attention, then
          delivers a clear strategic purpose.
        </p>
      </motion.header>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-5 lg:grid-cols-12" data-id="element-485" data-mp="10gh8t02ho6g9">
        <motion.section initial={{
        opacity: 0,
        y: 18
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        delay: 0.16,
        duration: 0.6
      }} className="min-h-0 overflow-hidden rounded-3xl border border-[var(--brand)]/10 bg-white shadow-sm lg:col-span-9" aria-label="Teaser campaign hook questions and strategic purpose" data-id="element-486" data-mp="2qty9ce1qbjsm">
          <div className={`grid ${ROW_GRID} bg-[var(--brand)] px-5 py-3.5 text-white`} data-id="element-487" data-mp="2pueb1l2ybd9">
            {COLUMNS.map(column => {
            const Icon = column.icon;
            return <div key={column.label} className="flex items-center gap-2 pr-3" data-id="element-488" data-mp="qdatr3n8ybms">
                  <Icon className="h-3.5 w-3.5 text-white/70" aria-hidden="true" data-id="element-489" data-mp="qfetrai0yqhs" />
                  <p className="text-[10px] font-bold uppercase tracking-[0.1em]" data-id="element-490" data-mp="mcikiaedk6qi">
                    {column.label}
                  </p>
                </div>;
          })}
          </div>

          <div className="flex h-[calc(100%-2.75rem)] flex-col divide-y divide-[var(--brand)]/10" data-id="element-491" data-mp="clnncazlbmqa">
            {masterSnaks.map((snak, index) => <motion.article key={snak.name} initial={{
            opacity: 0,
            x: -12
          }} animate={{
            opacity: 1,
            x: 0
          }} transition={{
            delay: 0.28 + index * 0.08,
            duration: 0.42
          }} className={`grid min-h-0 flex-1 ${ROW_GRID} items-center px-5 py-2.5`} data-id="element-492" data-mp="15a1noxe5iv50" data-mpi={index}>
                <p className="pr-4 text-xs font-extrabold leading-snug text-[var(--fg)]" data-id="element-493" data-mp="1micbicdk135x">
                  {snak.name}
                </p>
                <p className="pr-4 text-xs font-semibold leading-snug text-[var(--brand)]" data-id="element-494" data-mp="1vklln14wuuis">
                  {snak.hookQuestion}
                </p>
                <p className="pr-1 text-xs font-medium leading-snug text-[var(--muted)]" data-id="element-495" data-mp="20idj0aobr8ud">
                  {snak.strategicPurpose}
                </p>
              </motion.article>)}
          </div>
        </motion.section>

        <motion.aside initial={{
        opacity: 0,
        x: 18
      }} animate={{
        opacity: 1,
        x: 0
      }} transition={{
        delay: 0.3,
        duration: 0.6
      }} className="flex min-h-0 flex-col lg:col-span-3" data-id="element-496" data-mp="2abavo06my3l2">
          <div className="mb-3" data-id="element-497" data-mp="25mxkf2yrnzy1">
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-[var(--brand)]" data-id="element-498" data-mp="25m4l2n250cpx">
              Live example
            </p>
            <h3 className="mt-1 text-lg font-extrabold text-[var(--fg)]" data-id="element-499" data-mp="2fd91zsbdi7ne">
              Interactive Snak
            </h3>
          </div>

          <figure className="relative min-h-0 flex-1 overflow-hidden rounded-[1.75rem] bg-[#16222E] shadow-[0_18px_45px_rgba(23,32,51,0.18)] ring-1 ring-black/5" data-id="element-500" data-mp="2af77mb8hylwm">
            <img src={LIVE_SNAK_IMAGE} alt="Teaser Snak opening on a mother and child with the line: two nights without sleep, seven minutes to decide" className="h-full w-full object-contain" data-id="element-501" data-mp="1imzsxgcjxjkv" />
          </figure>
        </motion.aside>
      </div>
    </InteriorSlide>;
}